import { Chess } from "chess.js";
import type { ClientGame, ClientGameCtx } from "./index";
import { sound } from "../sound";

const GLYPH: Record<string, string> = {
  p: "♟", n: "♞", b: "♝", r: "♜", q: "♛", k: "♚",
};
const FILES = ["a", "b", "c", "d", "e", "f", "g", "h"];

export class ChessClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private boardEl!: HTMLDivElement;
  private statusEl!: HTMLDivElement;
  private movesEl!: HTMLDivElement;
  private squares = new Map<string, HTMLDivElement>();
  private selected: string | null = null;
  private legalTargets = new Set<string>();
  private lastFen = "";
  private lastHistLen = 0;

  private get amWhite() {
    return this.ctx.room.state.chess.whiteSlot === this.ctx.mySlot;
  }
  private get myTurn() {
    return this.ctx.room.state.chess.turnSlot === this.ctx.mySlot;
  }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();

    const wrap = document.createElement("div");
    wrap.className = "chess-wrap";

    this.statusEl = document.createElement("div");
    this.statusEl.className = "chess-status";

    this.boardEl = document.createElement("div");
    this.boardEl.className = "chess-board";

    this.movesEl = document.createElement("div");
    this.movesEl.className = "chess-moves";

    const resign = document.createElement("button");
    resign.className = "ghost small";
    resign.textContent = "Resign";
    resign.onclick = () => {
      if (confirm("Resign this game?")) ctx.room.send("resign");
    };

    wrap.append(this.statusEl, this.boardEl, this.movesEl, resign);
    ctx.root.appendChild(wrap);

    this.buildGrid();
    this.sync();
  }

  unmount() {
    this.squares.clear();
  }

  private buildGrid() {
    this.boardEl.innerHTML = "";
    this.squares.clear();
    const ranks = this.amWhite ? [8, 7, 6, 5, 4, 3, 2, 1] : [1, 2, 3, 4, 5, 6, 7, 8];
    const files = this.amWhite ? FILES : [...FILES].reverse();
    for (const rank of ranks) {
      for (const file of files) {
        const sq = `${file}${rank}`;
        const cell = document.createElement("div");
        const dark = (FILES.indexOf(file) + rank) % 2 === 0;
        cell.className = `sq ${dark ? "dark" : "light"}`;
        cell.dataset.square = sq;
        cell.onclick = () => this.onSquare(sq);
        this.squares.set(sq, cell);
        this.boardEl.appendChild(cell);
      }
    }
  }

  sync() {
    const cs = this.ctx.room.state.chess;
    if (!cs.fen) return;

    // rebuild grid if our colour assignment changed (rematch)
    const wantWhiteBottom = this.amWhite;
    const firstSq = this.boardEl.firstElementChild as HTMLElement | null;
    const haveWhiteBottom = firstSq?.dataset.square === "a8";
    if (wantWhiteBottom !== haveWhiteBottom) this.buildGrid();

    const game = new Chess(cs.fen);
    this.renderPieces(game, cs.lastFrom, cs.lastTo);

    // sounds: a move landed
    if (cs.history.length > this.lastHistLen && this.lastHistLen > 0) sound.place();
    this.lastHistLen = cs.history.length;
    this.lastFen = cs.fen;

    // status line
    const phase = this.ctx.room.state.phase;
    if (phase === "results") {
      this.statusEl.textContent = "";
    } else if (cs.check) {
      this.statusEl.innerHTML = this.myTurn
        ? `<b class="warn">Check!</b> your move`
        : `<b class="warn">Check given</b> · opponent to move`;
    } else {
      this.statusEl.textContent = this.myTurn ? "Your move" : "Opponent thinking…";
    }
    this.statusEl.classList.toggle("mine", this.myTurn && phase === "playing");

    // move list
    this.movesEl.innerHTML = "";
    cs.history.forEach((san: string, i: number) => {
      if (i % 2 === 0) {
        const n = document.createElement("span");
        n.className = "mv-num";
        n.textContent = `${i / 2 + 1}.`;
        this.movesEl.appendChild(n);
      }
      const s = document.createElement("span");
      s.className = "mv";
      s.textContent = san;
      this.movesEl.appendChild(s);
    });
    this.movesEl.scrollLeft = this.movesEl.scrollWidth;

    if (phase !== "playing") this.clearSelection();
  }

  private renderPieces(game: Chess, from: string, to: string) {
    for (const [sq, cell] of this.squares) {
      const piece = game.get(sq as any);
      cell.classList.toggle("last", sq === from || sq === to);
      cell.classList.toggle("sel", sq === this.selected);
      cell.classList.toggle("target", this.legalTargets.has(sq));
      const has = cell.querySelector(".pc");
      const want = piece ? `${piece.color}${piece.type}` : "";
      if ((has?.getAttribute("data-p") || "") === want) continue;
      has?.remove();
      if (piece) {
        const p = document.createElement("div");
        p.className = `pc ${piece.color === "w" ? "white" : "black"}`;
        p.setAttribute("data-p", want);
        p.textContent = GLYPH[piece.type];
        cell.appendChild(p);
      }
    }
  }

  private onSquare(sq: string) {
    if (this.ctx.room.state.phase !== "playing" || !this.myTurn) return;
    const game = new Chess(this.ctx.room.state.chess.fen);
    const piece = game.get(sq as any);
    const myColor = this.amWhite ? "w" : "b";

    if (this.selected && this.legalTargets.has(sq)) {
      const promo = this.needsPromotion(game, this.selected, sq) ? "q" : undefined;
      this.ctx.room.send("move", { from: this.selected, to: sq, promotion: promo });
      sound.submit();
      this.clearSelection();
      this.repaintSelection(game);
      return;
    }

    if (piece && piece.color === myColor) {
      this.selected = sq;
      this.legalTargets = new Set(
        game.moves({ square: sq as any, verbose: true }).map((m: any) => m.to),
      );
      sound.key();
    } else {
      this.clearSelection();
    }
    this.repaintSelection(game);
  }

  private needsPromotion(game: Chess, from: string, to: string) {
    const p = game.get(from as any);
    return p?.type === "p" && (to[1] === "8" || to[1] === "1");
  }

  private clearSelection() {
    this.selected = null;
    this.legalTargets.clear();
  }

  private repaintSelection(game: Chess) {
    const cs = this.ctx.room.state.chess;
    this.renderPieces(game, cs.lastFrom, cs.lastTo);
  }

  results(ctx: ClientGameCtx): HTMLElement {
    this.ctx = ctx;
    const cs = ctx.room.state.chess;
    const wrap = document.createElement("div");
    wrap.className = "chess-wrap results-mini";
    const reasons: Record<string, string> = {
      checkmate: "Checkmate", stalemate: "Stalemate", repetition: "Draw — repetition",
      material: "Draw — insufficient material", draw: "Draw — 50-move rule", resign: "Resignation",
    };
    const r = document.createElement("div");
    r.className = "chess-status";
    r.textContent = reasons[cs.endReason] || "Game over";
    wrap.appendChild(r);

    // static final board
    this.boardEl = document.createElement("div");
    this.boardEl.className = "chess-board";
    wrap.appendChild(this.boardEl);
    this.buildGrid();
    if (cs.fen) this.renderPieces(new Chess(cs.fen), cs.lastFrom, cs.lastTo);
    return wrap;
  }
}
